import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Grid } from '@mui/material';
import { useFormik } from 'formik';
import {
  AcademicQualificationForms,
  LanguageQualificationForm,
  OtherTestForm,
  PublicationForms,
  MeritForms,
  AcademicRefereeForms,
  EmploymentForms,
  ProfessionalQualificationForms,
  TrainingForms,
  ActivityForms,
  AdditionalDocumentForms,
  ProgrammeChoiceForm,
  HKPFS,
  ResearchExperienceForm,
  DistinctiveScholarshipView,
} from '.';
import { APPLICATION_LIST_TEXT, APPLICATION_TEXT } from '../lang';
import {
  useGetStudentApplication,
  type StudentApplicationData,
} from '../../graphql';
import { ApplicantParticulars } from '../../../Assessment';
import {
  SideList,
  type ListSections,
  Accordion,
  Loading,
  Alert,
} from '../../../../components';
import {
  ApplicationDeclaration,
  type ApplicationDeclarationData,
} from '../../../Application/components';

type StudentApplicationProps = {
  displayParticulars?: boolean;
};

export const StudentApplication: React.FunctionComponent<
  StudentApplicationProps
> = (props: StudentApplicationProps) => {
  const { displayParticulars = true } = props;
  const { applicationId } = useParams();

  const [clickedForm, setClickedForm] = useState<string>(
    APPLICATION_LIST_TEXT.APPLICATION_PARTICULARS,
  );

  const { studentApplication, loading, error } = useGetStudentApplication(
    applicationId as string,
  );

  const handleViewForm = (item: string): void => {
    setClickedForm(item);
  };

  const initialStudentApplication: StudentApplicationData = {
    academicQualifications: [],
    languageQualification: undefined,
    otherTest: undefined,
    publications: [],
    merits: [],
    academicReferees: [],
    researchExperience: {
      pastResearchExperience: '',
      proposedResearchTopic: '',
      proposedResearchPlan: '',
      reasons: '',
      longTermCareerPlan: '',
      expectedContribution: '',
      others: '',
      researchProposal: '',
    },
    employments: [],
    professionalQualifications: [],
    activities: [],
    trainings: [],
    additionalDocuments: [],
    hkpfsInformation: undefined,
    distinctiveScholarships: [],
    applicationDeclaration: undefined,
  };

  let {
    programmeChoice,
    academicQualifications,
    languageQualification,
    otherTest,
    publications,
    merits,
    academicReferees,
    employments,
    professionalQualifications,
    trainings,
    activities,
    additionalDocuments,
    hkpfsInformation,
    researchExperience,
    distinctiveScholarships,
    applicationDeclaration,
  } = initialStudentApplication;
  if (
    studentApplication !== undefined &&
    Object.keys(studentApplication).length > 0
  ) {
    programmeChoice = studentApplication.programmeChoice;
    academicQualifications = studentApplication.academicQualifications;
    languageQualification = studentApplication.languageQualification;
    otherTest = studentApplication.otherTest;
    publications = studentApplication.publications;
    merits = studentApplication.merits;
    academicReferees = studentApplication.academicReferees;
    employments = studentApplication.employments;
    professionalQualifications = studentApplication.professionalQualifications;
    trainings = studentApplication.trainings;
    activities = studentApplication.activities;
    additionalDocuments = studentApplication.additionalDocuments;
    hkpfsInformation = studentApplication.hkpfsInformation;
    researchExperience = studentApplication.researchExperience;
    distinctiveScholarships = studentApplication.distinctiveScholarships;
    applicationDeclaration = studentApplication.applicationDeclaration;
  }

  const isHKPFSApplicant = Boolean(hkpfsInformation?.HKPFSReferenceNumber);

  const componentWithAccoudion = (
    title: string,
    details: React.ReactNode,
  ): React.ReactNode => {
    return <Accordion title={title} details={details} defaultExpanded />;
  };

  const declaration: ApplicationDeclarationData = {
    personalType: applicationDeclaration?.personalType ?? undefined,
    confirmation: applicationDeclaration?.confirmation ?? false,
    informationAccuracy: applicationDeclaration?.informationAccuracy ?? false,
    engagement: applicationDeclaration?.engagement ?? undefined,
  };

  const declarationFormik = useFormik<ApplicationDeclarationData>({
    initialValues: declaration,
    enableReinitialize: true,
    onSubmit: async values => {},
  });

  useEffect(() => {
    if (!loading)
      declarationFormik.resetForm({
        values: {
          ...declaration,
        },
      });
  }, [loading]);

  const componentContainerMap: Record<string, React.ReactNode> = {
    ...(displayParticulars
      ? {
          [APPLICATION_LIST_TEXT.APPLICATION_PARTICULARS]: (
            <ApplicantParticulars />
          ),
        }
      : {}),

    [APPLICATION_LIST_TEXT.SECTION_CONTENT.HKPFS]: componentWithAccoudion(
      APPLICATION_LIST_TEXT.SECTION_CONTENT.HKPFS_FULL,
      <HKPFS
        HKPFSReferenceNumber={hkpfsInformation?.HKPFSReferenceNumber ?? '-'}
      />,
    ),

    [APPLICATION_LIST_TEXT.SECTION_CONTENT.DISTINCTIVE_SCHOLARSHIP]:
      componentWithAccoudion(
        APPLICATION_LIST_TEXT.SECTION_CONTENT.DISTINCTIVE_SCHOLARSHIP,
        <DistinctiveScholarshipView
          distinctiveScholarships={distinctiveScholarships ?? []}
        />,
      ),

    [APPLICATION_LIST_TEXT.SECTION_CONTENT.RESEARCH_EXPERIENCE]:
      componentWithAccoudion(
        APPLICATION_TEXT.RESEARCH_EXPERIENCE.TITLE,
        <ResearchExperienceForm
          data={researchExperience}
          isHKPFSApplicant={isHKPFSApplicant}
        />,
      ),

    [APPLICATION_LIST_TEXT.SECTION_CONTENT.PROGRAMME_CHOICE]:
      componentWithAccoudion(
        APPLICATION_TEXT.PROGRAMME_CHOICE.SECTION_TITLE,
        <ProgrammeChoiceForm data={programmeChoice} />,
      ),
    [APPLICATION_LIST_TEXT.SECTION_CONTENT.ACADEMIC_QUALIFICATION]:
      componentWithAccoudion(
        APPLICATION_TEXT.ACADEMIC_QUALIFICATION.SECTION_TITLE,
        <AcademicQualificationForms data={academicQualifications} />,
      ),
    [APPLICATION_LIST_TEXT.SECTION_CONTENT.LANGUAGE_QUALIFICATION]:
      componentWithAccoudion(
        APPLICATION_TEXT.LANGUAGE_QUALIFICATION.TITLE,
        <LanguageQualificationForm data={languageQualification} />,
      ),
    [APPLICATION_LIST_TEXT.SECTION_CONTENT.OTHER_TEST]: componentWithAccoudion(
      APPLICATION_TEXT.OTHER_TEST.TITLE,
      <OtherTestForm data={otherTest} />,
    ),
    [APPLICATION_LIST_TEXT.SECTION_CONTENT.PUBLICATION]: componentWithAccoudion(
      APPLICATION_TEXT.PUBLICATION.TITLE,
      <PublicationForms data={publications} />,
    ),
    [APPLICATION_LIST_TEXT.SECTION_CONTENT.MERIT]: componentWithAccoudion(
      APPLICATION_TEXT.MERITS.TITLE,
      <MeritForms data={merits} />,
    ),
    [APPLICATION_LIST_TEXT.SECTION_CONTENT.ACADEMIC_REFEREE]:
      componentWithAccoudion(
        APPLICATION_TEXT.ACADEMIC_REFEREE.TITLE,
        <AcademicRefereeForms data={academicReferees} />,
      ),

    [APPLICATION_LIST_TEXT.SECTION_CONTENT.EMPLOYMENT]: componentWithAccoudion(
      APPLICATION_TEXT.EMPLOYMENT.TITLE,
      <EmploymentForms data={employments} />,
    ),

    [APPLICATION_LIST_TEXT.SECTION_CONTENT.PROFESSIONAL_QUALIFICATION]:
      componentWithAccoudion(
        APPLICATION_TEXT.PROFESSIONAL_QUALIFICATION.TITLE,
        <ProfessionalQualificationForms data={professionalQualifications} />,
      ),
    [APPLICATION_LIST_TEXT.SECTION_CONTENT.TRAINING]: componentWithAccoudion(
      APPLICATION_TEXT.TRAINING.TITLE,
      <TrainingForms data={trainings} />,
    ),
    [APPLICATION_LIST_TEXT.SECTION_CONTENT.ACTIVITY]: componentWithAccoudion(
      APPLICATION_TEXT.ACTIVITY.TITLE,
      <ActivityForms data={activities} />,
    ),

    [APPLICATION_LIST_TEXT.SECTION_CONTENT.ADDITIONAL_DOCUMENTS]:
      componentWithAccoudion(
        APPLICATION_TEXT.ADDITIONAL_DOCUMENTS.TITLE,
        <AdditionalDocumentForms data={additionalDocuments} />,
      ),

    [APPLICATION_LIST_TEXT.SECTION_CONTENT.APPLICATION_DECLARATIONS]: (
      <ApplicationDeclaration formik={declarationFormik} enableEdit={false} />
    ),
  };

  const listSections: ListSections[] = [
    ...(displayParticulars
      ? [
          {
            items: [APPLICATION_LIST_TEXT.APPLICATION_PARTICULARS],
          },
        ]
      : []),
    {
      title: APPLICATION_LIST_TEXT.SECTION_LIST.SECTION_A,
      items: [APPLICATION_LIST_TEXT.SECTION_CONTENT.PROGRAMME_CHOICE],
    },
    {
      title: APPLICATION_LIST_TEXT.SECTION_LIST.SECTION_B,
      items: [APPLICATION_LIST_TEXT.SECTION_CONTENT.ACADEMIC_REFEREE],
    },
    {
      title: APPLICATION_LIST_TEXT.SECTION_LIST.SECTION_C,
      items: [
        APPLICATION_LIST_TEXT.SECTION_CONTENT.HKPFS,
        APPLICATION_LIST_TEXT.SECTION_CONTENT.DISTINCTIVE_SCHOLARSHIP,
        // APPLICATION_LIST_TEXT.SECTION_B.RESEARCH_EXPERIENCE,
      ],
    },

    {
      title: APPLICATION_LIST_TEXT.SECTION_LIST.SECTION_D,
      items: [
        APPLICATION_LIST_TEXT.SECTION_CONTENT.ACADEMIC_QUALIFICATION,
        APPLICATION_LIST_TEXT.SECTION_CONTENT.LANGUAGE_QUALIFICATION,
        APPLICATION_LIST_TEXT.SECTION_CONTENT.OTHER_TEST,
      ],
    },
    {
      title: APPLICATION_LIST_TEXT.SECTION_LIST.SECTION_E,
      items: [
        APPLICATION_LIST_TEXT.SECTION_CONTENT.PUBLICATION,
        APPLICATION_LIST_TEXT.SECTION_CONTENT.MERIT,
        APPLICATION_LIST_TEXT.SECTION_CONTENT.PROFESSIONAL_QUALIFICATION,
        APPLICATION_LIST_TEXT.SECTION_CONTENT.TRAINING,
      ],
    },
    {
      title: APPLICATION_LIST_TEXT.SECTION_LIST.SECTION_F,
      items: [
        APPLICATION_LIST_TEXT.SECTION_CONTENT.EMPLOYMENT,
        APPLICATION_LIST_TEXT.SECTION_CONTENT.ACTIVITY,
      ],
    },
    {
      title: APPLICATION_LIST_TEXT.SECTION_LIST.SECTION_G,
      items: [APPLICATION_LIST_TEXT.SECTION_CONTENT.RESEARCH_EXPERIENCE],
    },
    {
      title: APPLICATION_LIST_TEXT.SECTION_LIST.SECTION_H,
      items: [APPLICATION_LIST_TEXT.SECTION_CONTENT.ADDITIONAL_DOCUMENTS],
    },
    {
      title: APPLICATION_LIST_TEXT.SECTION_LIST.SECTION_I,
      items: [APPLICATION_LIST_TEXT.SECTION_CONTENT.APPLICATION_DECLARATIONS],
    },
  ];

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return loading ? (
    <Loading />
  ) : (
    <>
      <div className="grid grid-cols-[4fr,1fr]">
        {/* from area */}
        <div>
          <Grid
            container
            direction="column"
            sx={{
              marginBottom: '1rem',
            }}
          >
            {componentContainerMap[clickedForm]}
          </Grid>
        </div>
        {/* list area */}
        <SideList
          listSections={listSections}
          activatedForm={clickedForm}
          onChangeActivated={handleViewForm}
        />
      </div>
    </>
  );
};
